import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/admin';
import { Shield, Plus, UserX, UserCheck, Crown, User } from 'lucide-react';

const AdminAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'ADMIN'
  });
  const [error, setError] = useState(null);
  const [isWarning, setIsWarning] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadAdmins();
  }, []);

  const loadAdmins = async () => {
    setLoading(true);
    setError(null);
    setIsWarning(false);
    try {
      const response = await adminApi.getAllAdmins();
      if (response?.success) {
        setAdmins(response.admins || []);
      } else {
        setAdmins([]);
      }
    } catch (error) {
      console.error('Error loading admins:', error);
      if (error?.status === 403) {
        setError('Access denied: you must be a SUPER_ADMIN to view the admin list.');
        setIsWarning(true);
      } else {
        setError('Failed to load admins: ' + (error.message || 'Unknown error'));
        setIsWarning(false);
      }
      // Set empty array on error to prevent crashes
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsWarning(false);

    if (!formData.username || !formData.email || !formData.password) {
      setError('Please fill all required fields');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      const response = await adminApi.createAdmin(formData);
      if (response?.success) {
        setSuccess('Admin created successfully!');
        setFormData({ username: '', email: '', password: '', role: 'ADMIN' });
        setShowCreateModal(false);
        loadAdmins();
      }
    } catch (error) {
      setError('Failed to create admin: ' + (error.message || 'Unknown error'));
    }
  };

  const handleDeactivate = async (adminId) => {
    if (!window.confirm('Are you sure you want to deactivate this admin?')) return;

    try {
      const response = await adminApi.deactivateAdmin(adminId);
      if (response?.success) {
        setSuccess('Admin deactivated successfully');
        loadAdmins();
      }
    } catch (error) {
      setError('Failed to deactivate admin: ' + error.message);
    }
  };

  const handleUpdateRole = async (adminId, newRole) => {
    if (!window.confirm(`Change role to ${newRole}?`)) return;

    try {
      const response = await adminApi.updateAdminRole(adminId, newRole);
      if (response?.success) {
        setSuccess('Role updated successfully');
        loadAdmins();
      }
    } catch (error) {
      setError('Failed to update role: ' + error.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold flex items-center">
          <Shield className="w-8 h-8 mr-3 text-blue-400" />
          Admin Management
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create Admin</span>
        </button>
      </div>

      {error && (
        <div className={`${isWarning ? 'bg-yellow-900/50 border-yellow-500' : 'bg-red-900/50 border-red-500'} border rounded-lg p-4 mb-6`}>
          <p className={isWarning ? 'text-yellow-400' : 'text-red-400'}>{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-900/50 border border-green-500 rounded-lg p-4 mb-6">
          <p className="text-green-400">{success}</p>
        </div>
      )}

      {/* Admins Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading admins...</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Username</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Last Login</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{admin.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{admin.username}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{admin.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {admin.role === 'SUPER_ADMIN' ? (
                          <Crown className="w-4 h-4 text-yellow-400" />
                        ) : (
                          <Shield className="w-4 h-4 text-blue-400" />
                        )}
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          admin.role === 'SUPER_ADMIN' 
                            ? 'bg-yellow-500/20 text-yellow-400' 
                            : 'bg-blue-500/20 text-blue-400'
                        }`}>
                          {admin.role}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        admin.is_active 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {admin.last_login ? new Date(admin.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {new Date(admin.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        {admin.role !== 'SUPER_ADMIN' && (
                          <>
                            <button
                              onClick={() => handleUpdateRole(admin.id, admin.role === 'ADMIN' ? 'SUPER_ADMIN' : 'ADMIN')}
                              className="p-1 text-yellow-400 hover:text-yellow-300"
                              title="Change Role"
                            >
                              <Crown className="w-4 h-4" />
                            </button>
                            {admin.is_active && (
                              <button
                                onClick={() => handleDeactivate(admin.id)}
                                className="p-1 text-red-400 hover:text-red-300"
                                title="Deactivate"
                              >
                                <UserX className="w-4 h-4" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && admins.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          No admins found. Create your first admin!
        </div>
      )}

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700">
            <h2 className="text-2xl font-bold mb-4">Create New Admin</h2>
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2">Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  placeholder="admin_username"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  placeholder="admin@example.com"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                />
              </div>
              <div>
                <label className="block text-gray-300 mb-2">Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                >
                  <option value="ADMIN">ADMIN</option>
                  <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ username: '', email: '', password: '', role: 'ADMIN' });
                    setError(null);
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg"
                >
                  Create Admin
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAdmins;

